/**
 * TodoBlock — live session todo list of the builtin agent
 * (plan agent-todo-context-panel §A.1; visual reference: PlanCard in
 * AgentDetailView, assembled from existing agent design tokens).
 *
 * Visibility (owner decision 2026-07-23): renders nothing only for an EMPTY
 * list. A fully finished plan STAYS visible so the user sees it is complete —
 * it just collapses by default (header + count) and expands on click, the same
 * chevron affordance the entity cards use (ExpandableEntityCard). An active
 * plan (any in_progress/pending row) is expanded by default.
 */
import type { JSX } from "react";
import type { TodoItem } from "@magnis/client-core";
import { Icon } from "../../components/ui";
import { PanelRegion } from "./PanelRegion";

function rowIcon(status: TodoItem["status"]): JSX.Element {
  switch (status) {
    case "completed":
      return <Icon name="circle-check" size={16} />;
    default:
      return <Icon name="circle" size={16} />;
  }
}

/** The list a reader cares about: what is still to do and what got done.
 *
 * A cancelled item is a plan the agent abandoned. Showing it spends a line on
 * work nobody will do, and counting it makes "3/7" mean something other than
 * progress. It stays in the table — the row is the agent's own record — but it
 * is not part of the list.
 * @tested-by: tst_fe_unit_panel_001
 */
function live(items: readonly TodoItem[]): readonly TodoItem[] {
  return items.filter((i) => i.status !== "cancelled");
}

export function TodoBlock({ items }: { readonly items: readonly TodoItem[] }): JSX.Element | null {
  const shown = live(items);
  const finished = shown.length > 0 && shown.every((i) => i.status === "completed");

  if (shown.length === 0) return null;

  const completedCount = shown.filter((i) => i.status === "completed").length;

  return (
    // @tested-by: tst_fe_todoblock_001
    // The header stays put and only the rows scroll — the shape all three
    // panel regions share, which is why it lives in PanelRegion.
    <PanelRegion
      title="Todo"
      iconName="tasks"
      count={`${String(completedCount)}/${String(shown.length)}`}
      // Todo's own rule: open while there is work left, closed once there is
      // not. Hypotheses and memory start closed regardless.
      defaultCollapsed={finished}
      complete={finished}
      testId="todo"
    >
      {shown.map((item, i) => {
        const inProgress = item.status === "in_progress";
        const muted = item.status === "completed";
        return (
          <div key={i} className="flex items-start gap-2.5 py-1.5">
            <span
              className={`mt-0.5 ${
                item.status === "completed"
                  ? "text-green"
                  : inProgress
                    ? "text-accent"
                    : "text-[var(--color-agent-center-text-muted)]"
              }`}
            >
              {rowIcon(item.status)}
            </span>
            <span
              className={`text-[13px] leading-5 ${
                inProgress
                  ? "text-accent"
                  : muted
                    ? "text-[var(--color-agent-text-muted)]"
                    : "text-[var(--color-agent-center-text)]"
              }`}
            >
              {item.content}
            </span>
          </div>
        );
      })}
    </PanelRegion>
  );
}
